/**
 * "Use one of my pictures" — the drawings a child has kept, offered inside the
 * other rooms.
 *
 * This is what stops the rooms being four separate apps. A thing you made in
 * one place can be brought into another and knocked over, or cut in half. What
 * a child made stays theirs and travels with them.
 */

import { useEffect, useState } from 'preact/hooks';
import type { JSX } from 'preact';
import type { Picture } from '../lib/store.ts';
import { store } from '../lib/store.ts';
import type { Scene } from '../lib/scene.ts';
import { decodeScene } from '../lib/scene.ts';
import { play } from '../engine/audio.ts';
import { ToyButton } from './controls.tsx';

interface Props {
  title: string;
  onPick: (scene: Scene) => void;
  onCancel: () => void;
}

export function PicturePicker({ title, onPick, onCancel }: Props): JSX.Element {
  const [pictures, setPictures] = useState<Picture[] | null>(null);

  useEffect(() => {
    let alive = true;
    void (async () => {
      const db = await store();
      const all = await db.list();
      if (!alive) return;
      // Only ones still on the shelf, and only ones whose marks were kept —
      // a flattened image cannot be turned back into something to play with.
      setPictures(all.filter((p) => p.archivedAt === null && p.scene !== null));
    })();
    return () => {
      alive = false;
    };
  }, []);

  const choose = (picture: Picture) => {
    const scene = picture.scene ? decodeScene(picture.scene) : null;
    if (!scene || scene.strokes.length === 0) return;
    play('sparkle', 0.6);
    onPick(scene);
  };

  return (
    <div class="sheet-scrim" onClick={onCancel} role="presentation">
      <div class="sheet" role="dialog" aria-modal="true" aria-label={title} onClick={(e) => e.stopPropagation()}>
        <h2 class="sheet__title">{title}</h2>

        {pictures === null && <p class="sheet__note">Looking…</p>}
        {pictures !== null && pictures.length === 0 && (
          <p class="sheet__note">Nothing kept yet. Draw something and tap the star.</p>
        )}

        {pictures !== null && pictures.length > 0 && (
          <div class="picker">
            {pictures.map((picture) => (
              <button key={picture.id} type="button" class="picker__item" onClick={() => choose(picture)}>
                <img src={picture.thumb} alt="One of your pictures" />
              </button>
            ))}
          </div>
        )}

        <div class="sheet__actions">
          <ToyButton label="Not now" onClick={onCancel} />
        </div>
      </div>
    </div>
  );
}
